import { useMemo } from 'react';

import { Divider } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import { Stage } from '@/components/Stage/Stage';
import type { CompositedSignalIssue } from '@/features/(signal)/signals/model';
import { MatrixView } from '@/features/(signal)/signals/ui/chart.matrix';
import { ChartView } from '@/features/(signal)/signals/ui/chart.trend';
import { SignalPageHeader } from '@/features/(signal)/signals/ui/signal-header';
import { SignalList } from '@/features/(signal)/signals/ui/signal-list';
import {
  FavoriteToggle,
  ShowNewToggle,
  ViewControlHeaderSorter,
  ViewControlHeaderTabs,
} from '@/features/(signal)/signals/ui/view-control-header';
import { useOptimisticSearchParam } from '@/hooks/pure/useOptimisticSearchParam';
import { useSignalsSearchParams } from '@/pages/workspace/signals/search-params-schema';

import { useComputedIssueList, useGlobalSignalIssueState } from '../hooks';
import { isNewSignalIssue } from '../lib';
import { IssueList } from './chart.issue-list';

export const SignalsPageContent = () => {
  const { dateSelection, compositedFilter } = useGlobalSignalIssueState();
  const [params, setParams] = useSignalsSearchParams();

  const { view, favorite, showNew, topCount } = params;
  const [activeSignalId] = useOptimisticSearchParam('signal');

  const { computedIssues, isLoading, lastUpdatedAt } = useComputedIssueList();

  // signal + favorite 필터 적용
  const filteredIssuesBase: CompositedSignalIssue[] = useMemo(() => {
    return computedIssues.filter((issue) => {
      // signal 필터: 'all'이거나 없으면 전체, 아니면 해당 signal에 속한 issue만
      if (activeSignalId && activeSignalId !== 'all') {
        if (!issue.signalIds.includes(activeSignalId)) return false;
      }
      // favorite 필터
      if (favorite && !issue.favorite) return false;
      return true;
    });
  }, [computedIssues, activeSignalId, favorite]);

  const listIssues = useMemo(
    () =>
      showNew
        ? filteredIssuesBase
        : filteredIssuesBase.filter((issue) => !isNewSignalIssue(issue)),
    [filteredIssuesBase, showNew],
  );

  return (
    <div className="flex flex-col bg-white h-screen overflow-y-scroll">
      {/*Signal setting, filter, date period selecter */}
      <SignalPageHeader
        lastUpdatedAt={lastUpdatedAt ? lastUpdatedAt.toJSON() : undefined}
      />
      <Divider />
      <div>
        <div className="flex flex-col items-center pt-4 pb-10 h-fit">
          <div className="space-y-5 p-4 w-full max-w-6xl">
            {/* Signal List */}
            <SignalList issues={computedIssues} />
            <div className="space-y-5 p-4 rounded-md border border-border h-full">
              <div className="flex justify-between w-full">
                {/* View Select */}
                <ViewControlHeaderTabs />

                {/* Favorites & Sort selecter */}
                <div className="flex items-center gap-2">
                  {view === 'list' ? (
                    <ShowNewToggle
                      checked={showNew}
                      onCheckedChange={(checked) => setParams({ showNew: checked })}
                    />
                  ) : null}
                  <FavoriteToggle />
                  <ViewControlHeaderSorter datePreset={dateSelection} />
                </div>
              </div>

              <Stage>
                {/* Chart Views - Loading */}
                <Stage.Scene status={isLoading}>
                  <div className="w-full h-64 flex items-center justify-center">
                    <Spinner size={24} />
                  </div>
                </Stage.Scene>
                {/* Chart Views - Issue List */}
                <Stage.Scene status={view === 'list'}>
                  <IssueList
                    data={listIssues}
                    dynamicFilterSet={compositedFilter}
                    datePreset={dateSelection}
                    emptyTitle={
                      !showNew && filteredIssuesBase.length > 0
                        ? 'No issues match these filters'
                        : undefined
                    }
                  />
                </Stage.Scene>
                {/* Chart Views - Trend chart */}
                <Stage.Scene status={view === 'chart'}>
                  <ChartView
                    data={filteredIssuesBase}
                    dynamicFilterSet={compositedFilter}
                    datePreset={dateSelection}
                    topCount={topCount}
                  />
                </Stage.Scene>
                {/* Chart Views - Matrix chart */}
                <Stage.Scene status={view === 'matrix'}>
                  <MatrixView
                    data={filteredIssuesBase}
                    dynamicFilterSet={compositedFilter}
                    datePreset={dateSelection}
                    topCount={topCount}
                  />
                </Stage.Scene>
              </Stage>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
